/**
 * Real `AuditAPI` implementation backed by the HTTP client (ADR-0019 §3.4 + §3.5).
 *
 * The wire route for both exports is `GET /v1/audit/:record_id/bundle`
 * — the signed-JSON bundle endpoint (ADR-0009 §1.4, ADR-0010 §4.4) —
 * with `?format=html` for the server-rendered HTML (ADR-0024). The
 * server returns the body + `Content-Disposition: attachment` (verified
 * at `services/api/src/http/routes/v1/audit-bundle.ts`).
 *
 * ADR-0227 — a day too large for one bundle exports as parts:
 * `bundleParts` reads `GET /v1/audit/:record_id/bundle/parts` (how the
 * day splits, and which part holds the record), and both exports take an
 * optional `part`.
 *
 * Note: list + single-record reads (`mandates.listObserved`,
 * `mandates.get`) stay on the mock until ADR-0020 routes them through
 * `/v1/audit` with a Discovery filter. ADR-0019 §1.5 / §3.4.
 */

import type { AuditRecordId } from '@sill/types';
import type {
  AuditAPI,
  AuditBundleExportOptions,
  AuditBundlePartsManifest,
} from '@sill/types/api';

import { ApiError, type HttpClient } from './http-client.js';

export interface RealAuditDeps {
  readonly http: HttpClient;
}

/** `part=k` query suffix (ADR-0227); empty when no part is requested. */
function partQuery(opts: AuditBundleExportOptions | undefined, lead: '?' | '&'): string {
  const p = opts?.part;
  return p !== undefined && Number.isSafeInteger(p) && p >= 1
    ? `${lead}part=${String(p)}`
    : '';
}

/** Narrowed-scope query suffix (legal-review follow-up 2026-10-25).
 *  Empty when no scope was requested. Mutually exclusive with `part`. */
function scopeQuery(opts: AuditBundleExportOptions | undefined, lead: '?' | '&'): string {
  const s = opts?.scope;
  if (s !== 'attack' && s !== 'caller') return '';
  const from = opts?.from;
  const to = opts?.to;
  if (typeof from !== 'string' || typeof to !== 'string') return '';
  const scope_lead = lead;
  const amp = scope_lead === '?' ? '&' : '&';
  return `${scope_lead}scope=${s}${amp}from=${encodeURIComponent(from)}${amp}to=${encodeURIComponent(to)}`;
}

function queryString(opts: AuditBundleExportOptions | undefined, format_prefix: string): string {
  // The format `?format=html` is already present in the HTML path; the
  // JSON path has no leading query. We handle both by letting the caller
  // tell us the current leading separator state.
  const lead = format_prefix.length > 0 ? '&' : '?';
  const scope = scopeQuery(opts, lead);
  if (scope.length > 0) return `${format_prefix}${scope}`;
  const part = partQuery(opts, lead);
  return `${format_prefix}${part}`;
}

export function createRealAuditApi(deps: RealAuditDeps): AuditAPI {
  return {
    async exportRecordJson(
      id: AuditRecordId,
      opts?: AuditBundleExportOptions,
    ): Promise<Blob> {
      // The server returns the signed bundle body as application/json
      // + Content-Disposition: attachment. The download() helper at
      // views/mandates/export.ts consumes the Blob and triggers the
      // browser download via an object URL + click.
      return await deps.http.requestBlob({
        method: 'GET',
        path: `/v1/audit/${encodeURIComponent(id)}/bundle${queryString(opts, '')}`,
      });
    },

    async exportBundleHtml(
      id: AuditRecordId,
      opts?: AuditBundleExportOptions,
    ): Promise<Blob> {
      // ADR-0024 — server-rendered HTML bundle. The HTML embeds the
      // canonical signed JSON in a `<script type="application/sill+json">`
      // block; cryptographic standing is identical to the JSON export.
      return await deps.http.requestBlob({
        method: 'GET',
        path: `/v1/audit/${encodeURIComponent(id)}/bundle${queryString(opts, '?format=html')}`,
      });
    },

    async bundleParts(id: AuditRecordId): Promise<AuditBundlePartsManifest> {
      const body = await deps.http.request<AuditBundlePartsManifest>({
        method: 'GET',
        path: `/v1/audit/${encodeURIComponent(id)}/bundle/parts`,
      });
      if (body === undefined) throw new ApiError('bad_response', null);
      return body;
    },
  };
}
